import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Button,
  Label,
  Alert,
  AlertDescription,
  Badge,
  toast,
  Tooltip,
  AlertTitle,
  DialogFooter,
  Typography,
  NativeSelect,
} from "@altimateai/lego";
import { useMutation } from "@tanstack/react-query";
import { createJiraTicket } from "@/helpers/apis";
import { DatastoreTypeEnum } from "@/pages/datastores/types";
import { Teammate } from "@/pages/mcp/mcpSlice";
import { Spinner, Stack } from "@altimateai/lego";
import { OpportunityDetails } from "@/components/closingLoop/types";
import { useAppState } from "@/modules/app/useAppContext";
import { DatamatesSelector } from "@/components/closingLoop/common/DatamatesSelector";
import { HelpSection } from "@/components/closingLoop/common/HelpSection";
import { FirstTimeSetupAlert } from "@/components/closingLoop/common/FirstTimeSetupAlert";
import { SetupRequiredAlert } from "@/components/closingLoop/common/SetupRequiredAlert";
import useTeammates from "@/pages/mcp/useTeammates";
import ExpandableText from "../expandableText/ExpandableText";
import { isFreeStack } from "@/helpers/tenant";

interface CreateTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  opportunity: OpportunityDetails;
  onOpenInPlayground?: (message: string, selectedDatamate: string) => void;
}

export default function CreateTicketModal({
  isOpen,
  onClose,
  opportunity,
  onOpenInPlayground,
}: CreateTicketModalProps) {
  const [platform, setPlatform] = useState<string>("jira");
  const [selectedDatamate, setSelectedDatamate] = useState<string>("");
  const [datamates, setDatamates] = useState<Teammate[]>([]);
  const { datastores } = useAppState();
  const { data: datamatesData, isPending: isLoadingDatamates } = useTeammates(isOpen);

  const datastoresData = useMemo(() => {
    return datastores?.filter(datastore => datastore.type === DatastoreTypeEnum.SNOWFLAKE) ?? [];
  }, [datastores]);

  useEffect(() => {
    setDatamates(() => {
      // Filter datamates which have Jira integration
      return (
        datamatesData?.filter(datamate => {
          return datamate.integrations?.some(integration => {
            return (
              integration.id === "jira" &&
              integration.tools?.some(tool => tool.key === "jira_create_issue")
            );
          });
        }) || []
      );
    });
  }, [datamatesData]);

  const { mutate: jiraTicketMutation, isPending: isCreatingTicket } = useMutation({
    mutationFn: () =>
      createJiraTicket(
        opportunity.impact?.opportunity.opportunity_id ?? opportunity.opportunity_id,
        opportunity.impact?.query_id,
        false,
        "create_jira_ticket"
      ),
    onSuccess: data => {
      const message = data?.content ?? "";
      onOpenInPlayground?.(message, selectedDatamate);
    },
    onError: error => {
      toast({
        title: "Failed to create Jira ticket",
        description: error instanceof Error ? error.message : String(error),
        variant: "destructive",
      });
    },
  });

  const handleOpenInPlayground = () => {
    jiraTicketMutation();
  };

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogContent
        className={"al-max-h-[80vh] al-overflow-y-auto !al-max-w-[640px]"}
        onClick={e => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>Create Ticket</DialogTitle>
        </DialogHeader>

        <div className="al-overflow-y-auto al-flex-1">
          <div className="al-space-y-5 al-p-2">
            {/* Opportunity Summary */}
            <Alert variant={"info"}>
              <AlertTitle className={"al-text-base al-flex al-gap-2 al-items-center"}>
                {opportunity.title}
                {opportunity.savings && (
                  <Badge variant="secondary" className="al-text-xs">
                    Save ${opportunity.savings.toFixed(2)}/mo
                  </Badge>
                )}
              </AlertTitle>
              <AlertDescription>
                <ExpandableText buttonClassName="al-text-primary al-text-[10px]" lines={3}>
                  {opportunity.description}
                </ExpandableText>
              </AlertDescription>
            </Alert>

            {/* Platform Selection */}
            <div className="al-space-y-2">
              <Label>Platform</Label>
              <NativeSelect
                size="sm"
                variant="ghost"
                options={[
                  {
                    label: "Jira",
                    value: "jira",
                  },
                ]}
                id={"platform-select"}
                value={platform}
                onChange={(value: string) => setPlatform(value)}
              />
            </div>

            {/* Select Datamate Section */}
            <div className="al-space-y-3">
              <Typography variant={"h3"} weight={"semibold"}>
                Select Datamate
              </Typography>
              {/* Loading states */}
              {isLoadingDatamates && (
                <Stack className="al-items-center">
                  <Spinner size="sm" />
                  <span>Loading datamates...</span>
                </Stack>
              )}

              {/* No connections state */}
              {(!datastoresData || datastoresData.length === 0) && !isFreeStack() && (
                <SetupRequiredAlert />
              )}

              {/* Datamate selection for existing users */}
              {!isLoadingDatamates && datastoresData && (
                <div className="al-p-3 al-bg-gray-50 al-rounded-lg">
                  <DatamatesSelector
                    datamates={datamates}
                    selectedDatamate={selectedDatamate}
                    onChange={setSelectedDatamate}
                  />
                </div>
              )}

              {/*First-time user with multiple connections */}
              {!isLoadingDatamates &&
                (!datamatesData || datamatesData.length === 0) &&
                datastoresData &&
                datastoresData.length > 1 && <FirstTimeSetupAlert />}
            </div>

            {/* Help Section - Collapsible */}
            <HelpSection />
          </div>
        </div>

        {/* Action Buttons */}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={e => {
              e.stopPropagation();
              onClose();
            }}
          >
            Cancel
          </Button>
          {onOpenInPlayground && (
            <Tooltip title={!selectedDatamate ? "Please select a datamate" : ""}>
              <Button
                onClick={e => {
                  e.stopPropagation();
                  handleOpenInPlayground();
                }}
                disabled={isCreatingTicket || !selectedDatamate}
              >
                {isCreatingTicket ? (
                  <>
                    <Spinner size={"sm"} className={"al-text-white"} /> Opening in Studio
                  </>
                ) : (
                  "Open in Studio"
                )}
              </Button>
            </Tooltip>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
